import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { Sparkles, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtCurrency, fmtNumber } from "@/lib/utils";

export default function SimulationDialog({ campaign, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/simulations/run", { campaign, days: 14 });
      setResult(data);
    } catch (e) {
      toast.error("Simulation failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) run(); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight">Campaign simulation</DialogTitle></DialogHeader>
        {busy && <div className="py-10 text-sm text-muted-foreground text-center">Running AI simulation against workspace baseline...</div>}
        {result && (
          <div className="space-y-5" data-testid="simulation-result">
            <div className="grid grid-cols-2 lg:grid-cols-4 grid-borders bg-card/30">
              <Stat label="Predicted sent" value={fmtNumber(result.predicted.sent)} />
              <Stat label="Accepted" value={fmtNumber(result.predicted.accepted)} sub={`${result.baseline_rates.acceptance}%`} />
              <Stat label="Meetings" value={fmtNumber(result.predicted.meetings)} sub={`${result.baseline_rates.meeting}%`} />
              <Stat label="Pipeline est." value={fmtCurrency(result.predicted.pipeline_estimate_usd)} tone="signal" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
              <div className="border border-border rounded-sm p-4">
                <div className="label-eyebrow mb-2">AI cost estimate</div>
                <div className="text-2xl font-display font-semibold">{fmtCurrency(result.cost_estimate.ai_usd)}</div>
                <div className="text-xs text-muted-foreground">{fmtNumber(result.cost_estimate.ai_tokens)} tokens · GPT-5.2</div>
              </div>
              <div className="border border-border rounded-sm p-4">
                <div className="label-eyebrow mb-2">Config</div>
                <div className="text-xs space-y-1">
                  <div>Daily limit: <span className="font-mono">{result.config_summary.daily_limit}</span></div>
                  <div>Sequence steps: <span className="font-mono">{result.config_summary.sequence_len}</span></div>
                  <div>Horizon: <span className="font-mono">{result.config_summary.days}d</span></div>
                </div>
              </div>
            </div>

            {result.risk_notes.length > 0 && (
              <div className="border border-amber-accent/40 bg-amber-accent/10 rounded-sm p-3">
                <div className="text-xs font-semibold text-amber-accent flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3.5 h-3.5" />Risks detected</div>
                <ul className="text-xs space-y-1 list-disc pl-4 text-amber-accent">
                  {result.risk_notes.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}

            <div>
              <div className="label-eyebrow mb-2">Sample AI-generated messages</div>
              <div className="space-y-3">
                {result.samples.map((s, i) => (
                  <div key={i} className="border border-border rounded-sm p-3 bg-card/30" data-testid={`sim-sample-${i}`}>
                    <div className="text-xs text-muted-foreground mb-1">{s.prospect} · {s.company} · <span className="font-mono text-signal-500">{s.score}</span></div>
                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{s.message}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, sub, tone }) {
  return (
    <div className="p-4">
      <div className="label-eyebrow mb-1">{label}</div>
      <div className={`text-2xl font-display font-semibold tracking-tightest ${tone === "signal" ? "text-signal-500" : ""}`}>{value}</div>
      {sub && <div className="text-[0.65rem] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
